import React, { useState } from "react";
import axios from "axios";
import { Lock, Mail, ShieldCheck, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { PRICE_LABEL, BUNDLE_LABEL } from "@/lib/useEbookAccess";
import { isPlayApp } from "@/lib/playBilling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Restore({ product }) {
  const { lang, t } = useLang();
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments/restore`, { email, origin_url: window.location.origin, lang });
      setSent(true); toast.success(t("paywall_restore_sent"));
    } catch (err) { toast.error(err.response?.status === 422 ? t("paywall_invalid_email") : t("payment_failed")); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="text-xs text-ochre hover:underline" data-testid={`paywall-restore-${product}`}>{t("paywall_restore")}</button>;
  return (
    <form onSubmit={submit} className="flex flex-wrap justify-center gap-2 w-full max-w-sm" data-testid="paywall-restore-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("paywall_email_placeholder")} className="flex-1 min-w-[180px] rounded-full border border-stoneLine bg-card px-4 py-2 text-sm" data-testid="paywall-restore-email" />
      <button type="submit" disabled={sent} className="inline-flex items-center gap-1.5 rounded-full border border-stoneLine px-4 py-2 text-sm hover:bg-sandDeep/50 disabled:opacity-50" data-testid="paywall-restore-submit"><Mail className="w-3.5 h-3.5" /> {t("paywall_restore_button")}</button>
    </form>
  );
}

/** Locked section: blurred teaser + unlock card. Play Billing inside the Play app, Stripe in the browser; Play app without Billing → notice only (policy-safe). */
export default function Paywall({ product, buy, buying, viaPlay, remaining, children, priceLabel = PRICE_LABEL, bundleLabel = BUNDLE_LABEL }) {
  const { t } = useLang();
  const playApp = !viaPlay && isPlayApp();
  return (
    <div className="relative mt-6 print:hidden" data-testid={`paywall-${product}`}>
      <div className="max-h-[560px] overflow-hidden select-none pointer-events-none blur-[6px] opacity-70 [mask-image:linear-gradient(to_bottom,black_30%,transparent)]" aria-hidden="true">{children}</div>
      <div className="absolute inset-x-0 top-10 flex justify-center px-4">
        <div className="w-full max-w-md rounded-3xl border border-stoneLine bg-sand/95 backdrop-blur-xl shadow-2xl p-6 text-center flex flex-col items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-ochre/15 text-ochre flex items-center justify-center"><Lock className="w-6 h-6" /></span>
          <h3 className="font-display font-bold text-2xl text-ink leading-tight">{t("paywall_title")}</h3>
          <p className="text-sm text-ink/80 leading-relaxed">{t("paywall_text").replace("{n}", remaining)}</p>
          {playApp ? (
            <p className="inline-flex items-center gap-2 rounded-2xl bg-ink/5 px-4 py-3 text-sm text-ink" data-testid={`paywall-app-notice-${product}`}><Smartphone className="w-5 h-5 shrink-0 text-ochre" /> {t("paywall_app_notice")}</p>
          ) : (
            <>
              <button onClick={() => buy(product)} disabled={buying} className="mt-1 inline-flex items-center gap-2 rounded-full bg-ink text-sand px-6 py-3 text-base font-semibold hover:bg-ochre transition-colors disabled:opacity-60" data-testid={`paywall-buy-${product}`}>
                {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {t("paywall_buy").replace("{price}", priceLabel)}
              </button>
              <button onClick={() => buy("ebook_bundle")} disabled={buying} className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2 text-sm font-semibold text-ink hover:bg-sandDeep/50 transition-colors disabled:opacity-60" data-testid={`paywall-buy-bundle-${product}`}>
                {t("paywall_bundle").replace("{price}", bundleLabel)}
              </button>
              <span className="text-[11px] text-muted-foreground">{viaPlay ? t("paywall_methods_play") : t("paywall_methods")}</span>
            </>
          )}
          {!viaPlay && <Restore product={product} />}
        </div>
      </div>
    </div>
  );
}
